import { NotificationResponseDto } from "../models/notification/NotificationResponseDto";

export const NOTIFICATION_GRID_STORAGE_KEY = "notificationsPage";

export type NotificationGroup = "COMMENT" | "APPROVAL" | "SHIPMENT" | "ALL";

export type NotificationGroupConfig = {
  key: NotificationGroup;
  label: string;
  color: string;
  typeTokens: string[];
  referenceTypeTokens: string[];
};

export const NOTIFICATION_GROUP_CONFIGS: NotificationGroupConfig[] = [
  {
    key: "ALL",
    label: "All",
    color: "default",
    typeTokens: [],
    referenceTypeTokens: [],
  },
  {
    key: "COMMENT",
    label: "Comments",
    color: "blue",
    typeTokens: ["MENTION", "COMMENT"],
    referenceTypeTokens: ["COMMENT", "APPLICATION_COMMENT", "SHIPMENT_COMMENT"],
  },
  {
    key: "SHIPMENT",
    label: "Shipment",
    color: "green",
    typeTokens: ["SHIPMENT_ACTION"],
    referenceTypeTokens: ["SHIPMENT_ACTION", "SHIPMENT_HISTORY"],
  },
  {
    key: "APPROVAL",
    label: "Approval",
    color: "green",
    typeTokens: ["APPROVAL", "APPROVE", "DUYET"],
    referenceTypeTokens: ["APPROVAL", "APPROVE", "DUYET"],
  },
];

export const NOTIFICATION_GROUP_OPTIONS = NOTIFICATION_GROUP_CONFIGS.map(({ key, label }) => ({
  label,
  value: key,
}));

const normalizeNotificationToken = (value?: string) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const includesAnyToken = (value: string | undefined, tokens: string[]) => {
  const normalized = normalizeNotificationToken(value);
  return tokens.some((token) => normalized.includes(token));
};

export const getNotificationGroup = (notification: NotificationResponseDto): NotificationGroup => {
  const matched = NOTIFICATION_GROUP_CONFIGS.find(
    (config) =>
      config.key !== "ALL" &&
      (includesAnyToken(notification.type, config.typeTokens) || includesAnyToken(notification.referenceType, config.referenceTypeTokens)),
  );

  return matched?.key ?? "ALL";
};

export const getNotificationGroupConfig = (group: NotificationGroup): NotificationGroupConfig =>
  NOTIFICATION_GROUP_CONFIGS.find((config) => config.key === group) ?? NOTIFICATION_GROUP_CONFIGS[NOTIFICATION_GROUP_CONFIGS.length - 1];

export const isNotificationRead = (notification: NotificationResponseDto) =>
  Boolean(notification.isRead ?? (notification as NotificationResponseDto & { read?: boolean }).read);

export const isNotificationInGroup = (notification: NotificationResponseDto, group: NotificationGroup) =>
  group === "ALL" || getNotificationGroup(notification) === group;

export const getNotificationGroupCounts = (items: NotificationResponseDto[]) =>
  NOTIFICATION_GROUP_CONFIGS.reduce(
    (counts, config) => ({
      ...counts,
      [config.key]: config.key === "ALL" ? items.length : items.filter((item) => getNotificationGroup(item) === config.key).length,
    }),
    {} as Record<NotificationGroup, number>,
  );
